import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import ResultsDisplay from "./ResultsDisplay";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";

type Mode = "username" | "bio" | "both";
type Theme = "funny" | "cool" | "aesthetic" | "professional" | "quirky" | "minimalist" | "bold" | "vintage" | "modern" | "edgy" | "mysterious" | "playful";

const themeOptions = [
  { value: "cool", label: "😎 Cool", description: "Smooth and impressive" },
  { value: "funny", label: "😂 Funny", description: "Humorous and witty" },
  { value: "aesthetic", label: "✨ Aesthetic", description: "Beautiful and artistic" },
  { value: "professional", label: "💼 Professional", description: "Formal and business-like" },
  { value: "quirky", label: "🎨 Quirky", description: "Unique and unconventional" },
  { value: "minimalist", label: "⚪ Minimalist", description: "Simple and clean" },
  { value: "bold", label: "🔥 Bold", description: "Daring and striking" },
  { value: "vintage", label: "📻 Vintage", description: "Classic and retro" },
  { value: "modern", label: "🚀 Modern", description: "Contemporary and trendy" },
  { value: "edgy", label: "⚡ Edgy", description: "Sharp and daring" },
  { value: "mysterious", label: "🌙 Mysterious", description: "Enigmatic and intriguing" },
  { value: "playful", label: "🎪 Playful", description: "Fun and energetic" },
];

const Generator = () => {
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<Mode>("both");
  const [theme, setTheme] = useState<Theme>("cool");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<{ usernames: string[]; bios: string[] } | null>(null);
  const { toast } = useToast();

  const handleGenerate = async () => {
    if (!input.trim()) {
      toast({
        title: "Input required",
        description: "Please describe your interests or personality traits",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      const { data, error } = await supabase.functions.invoke("generate-bio-username", {
        body: { input: input.trim(), mode, theme },
      });

      if (error) throw error;

      setResults(data);
    } catch (error: any) {
      console.error("Generation error:", error);
      toast({
        title: "Generation failed",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="w-full py-12 md:py-16" id="generator">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">
          Free AI Username Generator & Bio Maker for Social Media
        </h2>
        <div className="bg-card rounded-2xl shadow-lg p-6 md:p-8 space-y-6 animate-scale-in">
          {/* Ad Placeholder - Above Generator */}
          <div className="ad-slot w-full h-24 bg-muted/50 rounded-lg flex items-center justify-center text-sm text-muted-foreground border border-border">
            Advertisement Space
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="input" className="text-base font-semibold">
                Describe Yourself
              </Label>
              <Textarea
                id="input"
                placeholder="E.g., Nature lover, tech enthusiast, coffee addict, travel blogger, fitness junkie..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="min-h-[100px] resize-none"
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground text-right">{input.length}/500</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label className="text-base font-semibold">Generation Mode</Label>
                <RadioGroup value={mode} onValueChange={(v) => setMode(v as Mode)}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="both" id="both" />
                    <Label htmlFor="both" className="cursor-pointer font-normal">
                      Username & Bio
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="username" id="username" />
                    <Label htmlFor="username" className="cursor-pointer font-normal">
                      Username Only
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="bio" id="bio" />
                    <Label htmlFor="bio" className="cursor-pointer font-normal">
                      Bio Only
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold">Theme Style</Label>
                <Drawer>
                  <DrawerTrigger asChild>
                    <Button variant="outline" className="w-full justify-between">
                      <span>{themeOptions.find(t => t.value === theme)?.label || "Select Theme"}</span>
                      <Sparkles className="h-4 w-4 ml-2" />
                    </Button>
                  </DrawerTrigger>
                  <DrawerContent>
                    <DrawerHeader>
                      <DrawerTitle>Choose Your Theme Style</DrawerTitle>
                      <DrawerDescription>Select a theme that matches your personality</DrawerDescription>
                    </DrawerHeader>
                    <div className="px-4 pb-4 max-h-[60vh] overflow-y-auto">
                      <div className="grid gap-2">
                        {themeOptions.map((option) => (
                          <DrawerClose key={option.value} asChild>
                            <Button
                              variant={theme === option.value ? "default" : "outline"}
                              className="w-full justify-start text-left h-auto py-3"
                              onClick={() => setTheme(option.value as Theme)}
                            >
                              <div className="flex flex-col items-start">
                                <span className="font-semibold">{option.label}</span>
                                <span className="text-xs text-muted-foreground">{option.description}</span>
                              </div>
                            </Button>
                          </DrawerClose>
                        ))}
                      </div>
                    </div>
                    <DrawerFooter>
                      <DrawerClose asChild>
                        <Button variant="outline">Close</Button>
                      </DrawerClose>
                    </DrawerFooter>
                  </DrawerContent>
                </Drawer>
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-primary to-secondary hover:opacity-90 transition-opacity"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Generating...
                </>
              ) : (
                "✨ Generate Now"
              )}
            </Button>
          </div>

          {results && <ResultsDisplay results={results} mode={mode} />}

          {/* Ad Placeholder - Below Results */}
          <div className="ad-slot w-full h-24 bg-muted/50 rounded-lg flex items-center justify-center text-sm text-muted-foreground border border-border">
            Advertisement Space
          </div>
        </div>
      </div>
    </section>
  );
};

export default Generator;
